// AppIcon — another app's mark, standing on innernet's shelf.
//
// The ONE way to show a connected service anywhere on a marketing surface. It
// wears the superellipse (never `border-radius`), the brand's own ground, and
// enough padding that the mark breathes — see lib/brand/appIcon for why each of
// those is a decision rather than a default.
//
// No hooks → safe in server and client components alike.

import React from "react";
import "./appIcon.css";
import BrandIcon, { brandKey } from "../BrandIcon";
import OrganismMark from "./OrganismMark";
import { skinFor, SQUIRCLE, type AppIconSkin } from "../../lib/brand/appIcon";

export default function AppIcon({
  name,
  size = 48,
  label,
  className,
  style,
  skin: skinOverride,
  src,
}: {
  /** any free-form service name — resolved through the BrandIcon registry. */
  name: string;
  /** the tile's edge length in px. The mark scales with it. */
  size?: number;
  /** accessible name; defaults to the service name. */
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  /** per-surface override (a dark act may want a different ground). */
  skin?: Partial<AppIconSkin>;
  /**
   * A RASTER mark instead of a registry glyph — another product's own app icon
   * (the compare shelf, public/brand/compare). It is already an icon, so it
   * fills the tile edge to edge and the squircle clips it; the ground layer
   * still paints beneath so a transparent PNG stands on white, not on nothing.
   */
  src?: string;
}) {
  const key = brandKey(name) ?? name.toLowerCase();
  const skin = { ...(src ? { bg: "#FFFFFF", ink: "#1C1C1E", edge: true, scale: 1 } : skinFor(key)), ...skinOverride };
  const mark = Math.round(size * (skin.scale ?? 0.54));

  return (
    <span
      className={["app-icon", className].filter(Boolean).join(" ")}
      data-force={skin.force ? "1" : undefined}
      data-edge={skin.edge ? "1" : undefined}
      role="img"
      aria-label={label ?? name}
      style={{
        width: size,
        height: size,
        // the mark's colour — `inherit` lets innernet's own mark keep tracking
        // the live organism hue instead of being pinned here.
        color: skin.ink === "inherit" ? undefined : skin.ink,
        ["--ai-bg" as string]: skin.bg,
        ...style,
      }}
    >
      {/* The ground is its own layer so the squircle clips the FILL, not the
          mark — clipping the whole tile would shave a glyph that reaches the
          corners (Notion's box, Zed's triangle). */}
      <span className="app-icon__ground" aria-hidden />
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt=""
          width={mark}
          height={mark}
          className="app-icon__mark app-icon__mark--raster"
          loading="lazy"
          decoding="async"
        />
      ) : key === "innernet" ? (
        // ⚠️ THE BRAND GRADIENT, never the live hue. Everywhere else in the
        // product innernet's mark self-tints from the organism you are looking
        // at — which is right in the app and wrong here: an APP ICON is a fixed
        // identity, and on a comparison card the room is wearing the OTHER
        // product's colour, so a self-tinting mark would have shown innernet in
        // mem0's purple. This is the wordmark's own blue ramp: deeper at the
        // top, lighter at the foot.
        <OrganismMark size={mark} tone="gradient" className="app-icon__mark" />
      ) : (
        <BrandIcon name={name} size={mark} className="app-icon__mark" />
      )}
    </span>
  );
}

/** The squircle clip id, for surfaces that need to wear it directly. */
export const APP_ICON_CLIP = `url(#${SQUIRCLE.clipId})`;
